import {Injectable} from '@angular/core';
import {Observable, ReplaySubject} from "rxjs";
import {isDefined} from "../../libs/utils/epic.utils";

/**
 * Knows which HTTP requests a click started, so a button can stay locked until their answers are in. Only requests
 * subscribed while the click handler runs count - one sent later, e.g. after a confirmation dialog, is not seen.
 */
@Injectable({providedIn: 'root'})
export class PendingRequestService {

  private scope: PendingRequestScope | undefined;

  /** Runs the action; the result completes once every request it started has finished - undefined if it started none. */
  public capture(action: () => void): Observable<void> | undefined {
    const previous: PendingRequestScope | undefined = this.scope;
    const scope: PendingRequestScope = new PendingRequestScope();
    this.scope = scope;
    try {
      action();
    } finally {
      this.scope = previous;
      scope.close();
    }
    return scope.hasRequests() ? scope.whenDone() : undefined;
  }

  /** For pendingRequestInterceptor: registers a request with the running capture and returns what releases it. */
  public track(): () => void {
    const scope: PendingRequestScope | undefined = this.scope;
    return isDefined(scope) ? scope.add() : () => undefined;
  }
}

class PendingRequestScope {

  private readonly done: ReplaySubject<void> = new ReplaySubject<void>(1);
  private requests: number = 0;
  private running: number = 0;
  private closed: boolean = false;

  public add(): () => void {
    this.requests++;
    this.running++;
    let pending: boolean = true;
    return () => {
      if (pending) {
        pending = false;
        this.running--;
        this.completeWhenDone();
      }
    };
  }

  public close(): void {
    this.closed = true;
    this.completeWhenDone();
  }

  public hasRequests(): boolean {
    return this.requests > 0;
  }

  public whenDone(): Observable<void> {
    return this.done.asObservable();
  }

  private completeWhenDone(): void {
    if (this.closed && this.running === 0) {
      this.done.next();
      this.done.complete();
    }
  }
}
